import React from 'react';

type PaymentMethodsSkeletonProps = {
  variant?: 'desktop' | 'mobile';
  /** Number of payment method card placeholders to render */
  count?: number;
};

/** Desktop row-style payment method placeholder */
const DesktopPayMethodRowSkeleton: React.FC = () => (
  <div className="flex flex-row items-center border border-[#E9E9E9] rounded-md my-2 sm:h-[50px] px-2 relative overflow-hidden">
    <div className="absolute inset-0 w-full h-full animate-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent z-10 pointer-events-none" />
    <div className="mx-4 my-auto h-8 w-10 bg-gray-200/70 rounded-xl border border-[#E9E9E9]/70 animate-pulse flex-shrink-0" />
    <div className="w-1/4 my-auto h-4 bg-gray-200/70 rounded animate-pulse" />
    <div className="w-1/4 my-auto h-4 bg-gray-200/70 rounded animate-pulse" />
    <div className="w-1/4 my-auto h-4 bg-gray-200/70 rounded animate-pulse" />
    <div className="ml-auto mx-4 h-7 w-7 bg-gray-200/70 rounded-lg animate-pulse flex-shrink-0" />
  </div>
);

/** Mobile card-style payment method placeholder */
const MobilePayMethodCardSkeleton: React.FC = () => (
  <div className="flex flex-col bg-white border border-[#E9E9E9] rounded-md mb-2 p-3 relative overflow-hidden">
    <div className="absolute inset-0 w-full h-full animate-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent z-10 pointer-events-none" />
    <div className="flex flex-row items-center mb-3 gap-2">
      <div className="h-8 w-10 bg-gray-200/70 rounded-lg border border-[#E9E9E9]/70 animate-pulse flex-shrink-0" />
      <div className="h-4 bg-gray-200/70 rounded w-16 animate-pulse" />
      <div className="h-4 bg-gray-200/70 rounded w-28 animate-pulse ml-auto" />
    </div>
    <div className="flex flex-row gap-2">
      <div className="flex-1 h-9 bg-gray-200/70 rounded-lg animate-pulse" />
      <div className="flex-1 h-9 bg-gray-200/70 rounded-lg animate-pulse" />
    </div>
  </div>
);

/**
 * Aesthetic skeleton for Wallet payment methods while user / payment methods load.
 */
const PaymentMethodsSkeleton: React.FC<PaymentMethodsSkeletonProps> = ({
  variant = 'desktop',
  count = 3,
}) => {
  const isDesktop = variant === 'desktop';

  return (
    <div
      className="w-full relative overflow-hidden"
      role="status"
      aria-label="Loading payment methods"
    >
      <div className="absolute inset-0 w-full h-full animate-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent z-10 pointer-events-none opacity-30" />

      <div className={`h-6 bg-gray-200/70 rounded animate-pulse ${isDesktop ? 'w-48 mb-2' : 'w-52 mb-2'}`} />
      <div className={`flex ${isDesktop ? 'flex-row items-center' : 'flex-col'} gap-3 mb-6`}>
        <div className={`h-4 bg-gray-200/70 rounded animate-pulse ${isDesktop ? 'w-64' : 'w-full'}`} />
        {isDesktop ? (
          <div className="h-10 bg-gray-200/70 rounded-md w-48 animate-pulse ml-auto flex-shrink-0" />
        ) : (
          <div className="h-12 bg-gray-200/70 rounded-md w-full animate-pulse" />
        )}
      </div>

      <div className={isDesktop ? '' : 'mt-5'}>
        {Array.from({ length: count }).map((_, index) =>
          isDesktop ? (
            <DesktopPayMethodRowSkeleton key={index} />
          ) : (
            <MobilePayMethodCardSkeleton key={index} />
          )
        )}
      </div>

      <span className="sr-only">Loading payment methods...</span>
    </div>
  );
};

export default PaymentMethodsSkeleton;
